import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Stack,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Divider,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { mockUploadedFile, mockTemplates } from './mockData';

const ResearchUploadMock = ({ onNext, onFileUpload, uploadedFile }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [listName, setListName] = useState('');
  const [dragActive, setDragActive] = useState(false);

  const handleFileUpload = () => {
    // Simulate file upload
    onFileUpload?.(mockUploadedFile);
    setListName(mockUploadedFile.name.replace('.csv', ''));
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    handleFileUpload();
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplate(templateId);
    const template = mockTemplates.find(t => t.id === parseInt(templateId));
    if (template) {
      setListName(`${template.name} Research`);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* STEP 1 Header matching List Cleaner */}
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h6" 
          component="h1" 
          gutterBottom
          sx={{ 
            textTransform: 'uppercase',
            letterSpacing: '0.15em',
            fontWeight: 500,
            fontSize: '0.875rem',
            color: '#FFFFFF'
          }}
        >
          STEP 1: UPLOAD YOUR LIST
        </Typography>
      </Box>
      
      {/* 50/50 Layout: Name field + templates on left, drag & drop on right */}
      <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
        {/* Left side: Name field and Template section */}
        <Box sx={{ 
          flex: 1, 
          backgroundColor: '#161c3e', // Match List Cleaner - same as Paper background
          borderRadius: 2,
          p: 3
        }}>
          {/* List Name Input Field */}
          <Box sx={{ mb: 3 }}>
            <TextField
              label="Research List Name"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="e.g., B2B SaaS Prospects Q1"
              required
              fullWidth
              variant="outlined"
              helperText="The results file you download will have this name"
              InputLabelProps={{
                sx: {
                  '&.Mui-required .MuiInputLabel-asterisk': {
                    display: 'none'
                  }
                }
              }}
            />
          </Box>

          {/* Template Selector section */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
              <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Use a Template
            </Typography>

            <FormControl sx={{ width: '100%', mb: 2 }}>
              <InputLabel id="template-select-label">Select Template</InputLabel>
              <Select
                labelId="template-select-label"
                value={selectedTemplate}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                label="Select Template"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {mockTemplates.map((template) => (
                  <MenuItem key={template.id} value={template.id}>
                    {template.name} {template.favorite && '⭐'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Button
              variant="contained"
              color="secondary"
              onClick={() => alert('Template would be applied')}
              disabled={!selectedTemplate || !uploadedFile || !listName.trim()}
              sx={{
                textTransform: 'none',
                borderRadius: '4px',
                fontWeight: 'medium',
                px: 3,
                width: '100%'
              }}
            >
              Apply Template
            </Button>
          </Box>
        </Box>

        {/* Right side: Drag & Drop area */}
        <Box sx={{ flex: 1 }}>
          <Paper 
            variant="outlined" 
            sx={{ 
              p: 4, 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#161c3e', // Match List Cleaner - same as Paper background
              border: `2px dashed ${dragActive ? 'rgba(67, 97, 238, 0.6)' : 'rgba(255, 255, 255, 0.1)'}`,
              borderRadius: 2,
              transition: 'all 0.2s ease',
              minHeight: '300px',
              cursor: 'pointer',
              '&:hover': {
                borderColor: 'rgba(67, 97, 238, 0.4)',
                backgroundColor: '#1a2046' // Slightly lighter on hover
              }
            }}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={handleFileUpload}
          >
            {!uploadedFile ? (
              <>
                <CloudUploadIcon 
                  sx={{ 
                    fontSize: 64, 
                    color: dragActive ? 'primary.light' : 'primary.main', 
                    mb: 2, 
                    transition: 'color 0.2s ease' 
                  }} 
                />
                <Typography variant="h6" gutterBottom>
                  Drag & drop CSV file here
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                  or click to browse and select a file
                </Typography>
                <Button 
                  variant="outlined" 
                  component="span"
                  sx={{ 
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'text.secondary',
                    textTransform: 'none',
                    borderRadius: '4px'  
                  }}
                >
                  Browse Files
                </Button>
              </>
            ) : (
              <>
                <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Upload Successful
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {uploadedFile.rowCount} rows and {uploadedFile.columnCount} columns detected
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {uploadedFile.name} • {uploadedFile.size}
                </Typography>
              </>
            )}
          </Paper>
        </Box>
      </Box>

      {/* Navigation */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Box /> {/* Empty box for spacing */}
        <Button
          variant="contained"
          color="primary"
          onClick={onNext}
          disabled={!uploadedFile || !listName.trim()}
          sx={{
            textTransform: 'none',
            borderRadius: '4px',
            fontWeight: 'medium',
            px: 3
          }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
};

export default ResearchUploadMock;